<template>
    <ConfigurationModal
        :is-open="isOpen"
        :configuration-tip-link="$t('funnel.merge.field.tipLink')"
        :configuration-tip-text="$t('funnel.merge.field.tipText')"
        :title="$t('funnel.merge.field')"
        :funnel-id="funnelId"
        class="merge-field-modal"
        @close-configuration-modal="closeMergeFieldModal"
    >
        <template #configurationParameters>
            <div
                v-for="(mergeField, i) in funnelMergeFields"
                :key="i"
                class="merge-field-container"
            >
                <DsInputField
                    v-model="mergeField.label"
                    :data-qa="`label_${mergeField.id}`"
                    :label="$t('funnel.merge.field.label')"
                    required
                    :submitted="mergeField.isSubmitted"
                    @input="handleInput(i)"
                >
                    <template #error>
                        {{ $t('funnel.merge.field.errRequired') }}
                    </template>
                </DsInputField>

                <DsInputField
                    v-model="mergeField.value"
                    :data-qa="`value_${mergeField.id}`"
                    :label="$t('funnel.merge.field.value')"
                    required
                    :submitted="mergeField.isSubmitted"
                    @input="handleInput(i)"
                >
                    <template #error>
                        {{ $t('funnel.merge.field.errRequired') }}
                    </template>
                </DsInputField>
                <DsTooltip position="top">
                    <template #reference>
                        <DsIconButton
                            class="edit-field"
                            aria-label="edit an item"
                            :data-qa="`save_${mergeField.id}`"
                            :name="mergeField.isEditing ? 'save' : 'check'"
                            :disabled="disableSave"
                            @click="saveCampaignMergeField(i)"
                        />
                    </template>
                    <template #default>
                        <div> {{ $t('funnel.mergeField.save') }}</div>
                    </template>
                </DsTooltip>

                <DsTooltip position="top">
                    <template #reference>
                        <DsIconButton
                            class="delete-field"
                            aria-label="delete an item"
                            :data-qa="`delete_${mergeField.id}`"
                            name="trash-2"
                            @click="deleteCampaignMergeField(i,mergeField.id)"
                        />
                    </template>
                    <template #default>
                        <div> {{ $t('funnel.mergeField.delete') }}</div>
                    </template>
                </DsTooltip>
            </div>

            <div v-if="isUnableToDelete" class="error-alert-container">
                <DsInlineAlert
                    type="critical"
                    leading-icon="alert-circle"
                    @close="closeAlertError"
                >
                    {{ $t('funnel.mergeField.inUse') }} {{ marketingEmailLabels }}
                </DsInlineAlert>
            </div>

            <span
                class="add-field"
                @click="addCampaignMergeField"
            >
                <DsIcon name="add" />
                <span>{{ $t('funnel.merge.field.add') }}</span>
            </span>
        </template>
    </ConfigurationModal>
</template>

<script>
import ConfigurationModal from '../ConfigurationModal.vue';
import {
    loadMergeFields,
    saveMergeField,
    deleteMergeField,
    checkMarketingEmailIdsUsingMergeField,
} from './mergefields.api';
import { executeOnAllMatchingEmails } from '../../utils/cell/automation-mapper-utils';

export default {
    components: {
        ConfigurationModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: Number,
            default: 0,
        },
        draftJson: {
            type: Object,
            default: () => ({}),
        },
    },

    emits: ['close-merge-fields', 'mark-dirty'],

    data() {
        return {
            funnelMergeFields: [],
            isUnableToDelete: false,
            marketingEmailLabels: [],
            disableSave: false,
        };
    },

    async mounted() {
        await this.loadCampaignMergeFields();
    },

    methods: {
        async loadCampaignMergeFields() {
            try {
                this.funnelMergeFields = await loadMergeFields(this.funnelId);

                const mergeFields = this.funnelMergeFields.map((mergeField) => ({
                    ...mergeField,
                    isEditing: true,
                    isSubmitted: false,
                }));

                this.funnelMergeFields = mergeFields;
            } catch (error) {
                this.$error({ message: this.$t('funnel.merge.field.loading.error') });
            }
        },

        closeMergeFieldModal() {
            this.isUnableToDelete = false;
            this.$emit('close-merge-fields');
        },

        async saveCampaignMergeField(index) {
            const mergeField = this.funnelMergeFields[index];

            this.isUnableToDelete = false;

            if (mergeField.label && mergeField.value) {
                const payload = {
                    label: mergeField.label,
                    value: mergeField.value,
                };

                try {
                    const mergeFieldId = await saveMergeField(this.funnelId, mergeField.id, payload);

                    mergeField.id = mergeFieldId;

                    const marketingEmailIds = await this.checkMarketingEmailIdsUsingMergeField(mergeFieldId);

                    executeOnAllMatchingEmails(marketingEmailIds, this.draftJson, () => {
                        this.markDirty();
                    });

                    mergeField.isEditing = false;
                } catch (error) {
                    this.$error({ message: this.$t('funnel.merge.field.save.failure') });
                }
            } else {
                mergeField.isSubmitted = true;
                this.disableSave = true;
            }
        },

        async deleteCampaignMergeField(index, mergeFieldId) {
            if (mergeFieldId) {
                const marketingEmailIds = await this.checkMarketingEmailIdsUsingMergeField(mergeFieldId);

                try {
                    if (marketingEmailIds.length === 0) {
                        await deleteMergeField(mergeFieldId);
                        await this.loadCampaignMergeFields();
                    } else {
                        const marketingEmailLabelsArray = [];

                        executeOnAllMatchingEmails(marketingEmailIds, this.draftJson, (cell) => {
                            marketingEmailLabelsArray.push(cell);
                        });

                        this.marketingEmailLabels = marketingEmailLabelsArray.join(', ');
                        this.isUnableToDelete = true;
                    }
                } catch (error) {
                    this.$error({ message: this.$t('funnel.merge.field.delete.failure') });
                }
            } else {
                this.funnelMergeFields.splice(index, 1);
            }
        },

        async checkMarketingEmailIdsUsingMergeField(mergeFieldId) {
            try {
                return await checkMarketingEmailIdsUsingMergeField(mergeFieldId);
            } catch (error) {
                this.$error({ message: this.$t('funnel.merge.field.check.email.failure') });

                return false;
            }
        },

        addCampaignMergeField() {
            const newMergeField = {
                id: 0,
                label: '',
                value: '',
                isEditing: true,
                isSubmitted: false,
            };

            this.disableSave = false;
            this.isUnableToDelete = false;
            this.funnelMergeFields = [...this.funnelMergeFields, newMergeField];
        },

        closeAlertError() {
            this.isUnableToDelete = false;
        },

        handleInput(index) {
            this.disableSave = false;
            this.funnelMergeFields[index].isEditing = true;
        },

        markDirty() {
            this.$emit('mark-dirty');
        },
    },
};
</script>

<style lang="scss">
div.merge-field-modal  div.modal-body-inner,
div.merge-field-modal  div.modal-body {
    padding: 0px;
}

div.merge-field-container section.tooltip {
    border-radius: 0.75rem;
    padding: 0.75rem;
}

div.merge-field-container section.tooltip div.arrow {
    filter: none;
    border: none;
}
</style>

<style lang="scss" scoped>
.merge-field-container {
    display: inline-flex;
    width: 100%;
    padding: 20px;
    border-bottom: 1px solid $color-gray-300;
}

.merge-field-container > div.input-field {
    margin: 5px;
}

.edit-field, .delete-field {
    margin-top: 5px;
}

.add-field {
    align-items: center;
    color: #0042db;
    cursor: pointer;
    display: inline-flex;
    font-weight: 500;
    grid-gap: 0.5rem;
    padding: 20px;
}
.error-alert-container {
    padding: 20px;
}
</style>
